import React from "react";
import { Input, Checkbox, Spin, Switch, Modal, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";

/**
 * B2B customer search input + dropdown results panel.
 * Used in both AddCustomerDrawer and EditCustomerDrawer on the Business tab.
 */
const B2BCustomerSearch = ({
  isB2BEnabled,
  setIsB2BEnabled,
  panCustomerInfo,
  lastSearchedValue,
  setLastSearchedValue,
  isViewMode,
  customerConfigData,
  searchByName,
  setSearchByName,
  b2bLoading,
  b2bSearchResults,
  setB2bSearchResults,
  showB2BDropdown,
  setShowB2BDropdown,
  searchContainerRef,
  selectedB2BCustomer,
  setSelectedB2BCustomer,
  activeTab,
  customerForm,
  setFormDataCache,
  setLoading,
  searchB2BCustomers,
  validateGSTNumber,
  mapGSTToFormValuesDynamic,
  mapB2BDataDynamic,
  groupedFields,
  setInvoiceAddressList,
  setDeliveryAddressList,
  setSelectedInvoiceAddress,
  setSelectedDeliveryAddress,
  gstInfo,
  setGstInfo,
  // Optional: when B2BCustomerSearch is used in EditCustomerDrawer, the B2B switch
  // may be locked when PAN is valid. Pass lockB2BSwitch=true in that case.
  lockB2BSwitch,
  isPanCustomer,
  setSwitchToggle,
}) => {
  console.log("panCustomerInfo ----->", panCustomerInfo);
  const handleB2BSwitchChange = async (checked) => {
    if (checked && lastSearchedValue?.trim() && !searchByName) {
      try {
        setLoading(true);
        const gstResponse = await validateGSTNumber(lastSearchedValue.trim());
        if (gstResponse?.valid) {
          if (setGstInfo) setGstInfo(gstResponse);
          setIsB2BEnabled(true);
          if (setSwitchToggle) setSwitchToggle(true);
        } else {
          Modal.error({ title: "GST Validation Failed", content: "The GST number could not be validated. Please check and try again." });
        }
      } catch {
        Modal.error({ title: "GST Validation Error", content: "Unable to validate GST number. Please try again." });
      } finally {
        setLoading(false);
      }
      return;
    }
    setIsB2BEnabled(checked);
    if (!checked && setGstInfo) setGstInfo(null);
  };

  const gstStatusBadge = () => {
    if (!gstInfo?.gstStatus) return null;
    const s = gstInfo.gstStatus.toUpperCase();
    const color = s === "ACTIVE" ? "green" : s === "CANCELLED" ? "red" : "orange";
    return (
      <Tag color={color} style={{ marginLeft: 8, fontSize: "12px" }}>
        {gstInfo?.gstStatus}
      </Tag>
    );
  };

  const handleGSTEnter = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();
    if (!searchByName && lastSearchedValue.trim()) {
      try {
        setLoading(true);
        const gstResponse = await validateGSTNumber(lastSearchedValue.trim());
        if (gstResponse?.valid) {
          if (setGstInfo) setGstInfo(gstResponse);
          setIsB2BEnabled(true);
          if (setSwitchToggle) setSwitchToggle(true);
          const mappedValues = mapGSTToFormValuesDynamic(gstResponse, groupedFields);
          customerForm.setFieldsValue(mappedValues);
          setFormDataCache((prev) => ({
            ...prev,
            [activeTab]: { ...(prev[activeTab] || {}), ...mappedValues },
          }));
          setB2bSearchResults([]);
          setShowB2BDropdown(false);
          if (b2bSearchResults.length > 0) {
            setSelectedB2BCustomer(b2bSearchResults[0]);
          }
        }
      } catch (err) {
        console.error("GST validation failed", err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setLastSearchedValue(v);
    setSelectedB2BCustomer(null);
    if (v.trim() !== "") {
      setShowB2BDropdown(true);
      searchB2BCustomers(v);
    } else {
      setB2bSearchResults([]);
      setShowB2BDropdown(false);
      customerForm.resetFields();
      setFormDataCache((prev) => ({ ...prev, [activeTab]: {} }));
    }
  };

  const handleInputClear = () => {
    setLastSearchedValue("");
    setB2bSearchResults([]);
    setSelectedB2BCustomer(null);
    customerForm.resetFields();
    setFormDataCache((prev) => ({ ...prev, [activeTab]: {} }));
  };

  const handleSearchByNameChange = (e) => {
    setSearchByName(e.target.checked);
    setB2bSearchResults([]);
    setSelectedB2BCustomer(null);
    customerForm.resetFields();
    setFormDataCache((prev) => ({ ...prev, [activeTab]: {} }));
  };

  const handleCustomerSelect = async (customer) => {
    setB2bSearchResults([]);
    setLastSearchedValue(customer.customerName);
    setSelectedB2BCustomer(customer);
    if (setInvoiceAddressList) setInvoiceAddressList(customer.invoiceAddresses || []);
    if (setDeliveryAddressList) setDeliveryAddressList(customer.deliveryAddresses || []);
    if (setSelectedInvoiceAddress) setSelectedInvoiceAddress(null);
    if (setSelectedDeliveryAddress) setSelectedDeliveryAddress(null);
    const mappedValues = mapB2BDataDynamic(customer, groupedFields);
    const currentValues = customerForm.getFieldsValue();
    const b2bFields =
      isPanCustomer && isB2BEnabled
        ? {
            b2bCustomerId: customer.customerId,
            b2bCustomerName: customer.customerName,
            b2bCustomerCode: customer.customerCode,
          }
        : {};

    customerForm.setFieldsValue({
      ...currentValues,
      ...mappedValues,
      ...b2bFields,
    });
    setFormDataCache((prev) => ({
      ...prev,
      [activeTab]: {
        ...(prev[activeTab] || {}),
        ...mappedValues,
        ...b2bFields,
      },
    }));
  };

  return (
    <div
      style={{
        paddingLeft: "1px",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "25px",
        marginBottom: "16px",
      }}
      ref={searchContainerRef}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "1.5vw" }}>
        <div style={{ fontWeight: 600, fontSize: "16px" }}>Business Customer (B2B)</div>
        <Switch checked={isB2BEnabled} onChange={handleB2BSwitchChange} />
        {gstStatusBadge()}
      </div>

      {isB2BEnabled && (
        <div style={{ position: "relative", width: "60%" }}>
          <Input
            placeholder={searchByName ? "Search B2B Customers by Name..." : "Search B2B Customers by GST No..."}
            allowClear
            value={lastSearchedValue}
            disabled={isViewMode || !customerConfigData?.b2bSettings?.enableB2BCustomerRegistration}
            onKeyDown={handleGSTEnter}
            onChange={handleInputChange}
            onClear={handleInputClear}
            loading={b2bLoading}
            prefix={<SearchOutlined style={{ color: "#a0a0a0" }} />}
            style={{
              height: "30px",
              borderRadius: "6px",
              fontSize: "14px",
              width: "65%",
              backgroundColor: !customerConfigData?.b2bSettings?.enableB2BCustomerRegistration ? "#f5f5f5" : "#fff",
              cursor: !customerConfigData?.b2bSettings?.enableB2BCustomerRegistration ? "not-allowed" : "text",
            }}
          />
          <Checkbox checked={searchByName} style={{ marginLeft: "1vw" }} onChange={handleSearchByNameChange} disabled={isViewMode}>
            Search by Name
          </Checkbox>

          {showB2BDropdown && lastSearchedValue?.trim() !== "" && !selectedB2BCustomer && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                maxHeight: "300px",
                overflowY: "auto",
                background: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                marginTop: "4px",
                zIndex: 10,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                padding: "8px 0",
                width: "65%",
              }}
            >
              {b2bLoading && (
                <div style={{ textAlign: "center", padding: "12px" }}>
                  <Spin size="small" /> Searching...
                </div>
              )}

              {!b2bLoading && b2bSearchResults.length > 0 && (
                <>
                  {b2bSearchResults.map((customer) => (
                    <div
                      key={customer.customerId}
                      style={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #f0f0f0",
                        cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f9f9f9")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      <div style={{ fontWeight: 600, fontSize: "15px", color: "#1a1a1a" }}>{customer.customerName}</div>
                      <div style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}>
                        <strong>Code:</strong> {customer.customerCode}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {!b2bLoading && lastSearchedValue?.trim() !== "" && b2bSearchResults.length === 0 && (
                <div style={{ textAlign: "center", padding: "12px", color: "#999" }}>No customers found</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default B2BCustomerSearch;
